import { useCallback, useRef } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import {
	creators,
	print,
	CLAUSE_TYPE_TERMINAL,
	COMPOUND_OPERATOR_AND,
	OPERATOR_EQUALS,
	OPERATOR_IN,
	OPERAND_EMPTY,
	type Clause,
} from '@atlaskit/jql-ast';
import { UnsupportedRefinementError } from '../unsupported-refinement-error/index.tsx';
import { useAST, useClauseMap, useJQL } from '../index.tsx';
import { getOperandValues } from '../get-operand-values.tsx';

const getOperand = (selectedValue: string) => {
	if (selectedValue === OPERAND_EMPTY) {
		return creators.keywordOperand();
	}

	return creators.byText.valueOperand(selectedValue);
};

const getOperands = (selectedValues: string[]) =>
	selectedValues.map((selectedValue) => getOperand(selectedValue));

const getInitialTotalCount = (clause?: Clause) => {
	if (clause?.clauseType === CLAUSE_TYPE_TERMINAL && clause.operand) {
		return getOperandValues(clause.operand).length;
	}

	return 0;
};

const createTerminalClause = (clauseName: string, selectedValues: string[]) =>
	creators.terminalClause(
		creators.field(clauseName),
		creators.operator(selectedValues.length > 1 ? OPERATOR_IN : OPERATOR_EQUALS),
		selectedValues.length > 1
			? creators.listOperand(getOperands(selectedValues))
			: getOperand(selectedValues[0]),
	);

/**
 * @deprecated use ast and clauses from value panel to update the jql
 */
export const useClauseUpdater = (clauseName: string) => {
	const ast = useAST();

	const jql = useJQL();

	const clauseMap = useClauseMap();

	const clauses = clauseMap[clauseName.toLowerCase()];

	if (clauses && clauses.length > 1) {
		throw new UnsupportedRefinementError(jql, `Too many clauses on update for ${clauseName}`);
	}

	const totalCount = useRef(getInitialTotalCount(clauses?.[0]));

	const addValueToClause = useCallback(
		(selectedValue?: string) => {
			if (!selectedValue) {
				return ast.represents;
			}

			if (clauses?.length) {
				const [clause] = clauses;

				if (clause.clauseType === CLAUSE_TYPE_TERMINAL && clause.operand) {
					const existingValues = getOperandValues(clause.operand);

					clause.replace(createTerminalClause(clauseName, [...existingValues, selectedValue]));
					totalCount.current = existingValues.length + 1;
				} else {
					clause.replace(createTerminalClause(clauseName, [selectedValue]));
					totalCount.current = 1;
				}
			} else {
				// This mitigates a race condition where multiple concurrent modifications to the AST
				// can result in duplicated clauses for the same field making it to the final JQL string
				// (which throws an error on the UI) – we will explore better longer term solutions as
				// part of https://jplat.atlassian.net/browse/EM-9488
				const astClone = cloneDeep(ast);

				astClone.query?.appendClause(
					createTerminalClause(clauseName, [selectedValue]),
					COMPOUND_OPERATOR_AND,
				);

				totalCount.current = 1;

				return print(astClone, {
					operatorCase: 'upper',
				});
			}

			return print(ast, {
				operatorCase: 'upper',
			});
		},
		[clauses, ast, clauseName],
	);

	const removeValueFromClause = useCallback(
		(selectedValue?: string) => {
			if (!selectedValue) {
				return ast.represents;
			}

			if (clauses?.length) {
				const [clause] = clauses;

				if (clause.clauseType === CLAUSE_TYPE_TERMINAL && clause.operand) {
					const existingValues = getOperandValues(clause.operand);

					const newSelectedValues = existingValues.filter((value) => value !== selectedValue);

					if (newSelectedValues.length) {
						clause.replace(createTerminalClause(clauseName, newSelectedValues));
						totalCount.current = newSelectedValues.length;
					} else {
						clause.remove();
						totalCount.current = 0;
					}
				} else {
					clause.remove();
					totalCount.current = 0;
				}
			} else {
				totalCount.current = 0;
			}

			return print(ast, {
				operatorCase: 'upper',
			});
		},
		[clauses, ast, clauseName],
	);

	const removeClause = useCallback(() => {
		if (clauses?.length) {
			const [clause] = clauses;

			clause.remove();
		}

		totalCount.current = 0;

		return print(ast, {
			operatorCase: 'upper',
		});
	}, [ast, clauses]);

	const getTotalCount = useCallback(() => {
		return totalCount.current;
	}, []);

	return { addValueToClause, removeValueFromClause, removeClause, getTotalCount };
};
